import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { tForComponent } from '@/utils/translations';
import { ArrowLeft } from 'react-feather';
import { Typography } from '@/UIlibrary/Typography/Typography';
import {
	CustomerRecord,
	SyncFincluCustomersRequest,
	SyncResult,
	ValidationErrorResponse
} from '../CustomerSupport/types';
import { z } from 'zod';
import { useSupabaseStore } from '../CustomerSupport/supabaseStore';
import Button from '@/UIlibrary/Button/Button';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import FileUploadSection from './components/FileUploadSection';
import ValidationErrorsList from './components/ValidationErrorsList';
import ErrorMessageDisplay from './components/ErrorMessageDisplay';
import CustomersPreviewTable from './components/CustomersPreviewTable';
import SyncResultsDisplay from './components/SyncResultsDisplay';
import { getLanguageFromPath } from '@/utils/utils';
import { DEFAULT_LANGUAGE } from '@/constants/languages';

const customerRecordSchema = z.object({
	client_id: z.string().min(1, 'Client ID is required'),
	names: z.string().min(1, 'Names is required'),
	first_last_name: z.string().min(1, 'First last name is required'),
	second_last_name: z.string().optional(),
	email: z.string().email().optional().or(z.literal('')),
	phone_number: z.string().min(1, 'Phone number is required'),
	marital_status: z.string().optional(),
	state: z.string().optional(),
	city: z.string().optional(),
	date_of_birth: z.string().optional(),
	started_at: z.string().optional(),
	approved_at: z.string().optional(),
	open_current_balance: z.union([z.string(), z.number()]).optional(),
	open_overdue_balance: z.union([z.string(), z.number()]).optional(),
	closed_maximum_balance: z.union([z.string(), z.number()]).optional(),
	monthly_payment: z.union([z.string(), z.number()]).optional(),
	credit_bureau_default_score: z.union([z.string(), z.number()]).optional(),
	total_trials: z.union([z.string(), z.number()]).optional(),
	months_overdue: z.union([z.string(), z.number()]).optional(),
	years_in_credit_bureau: z.union([z.string(), z.number()]).optional(),
	utilization_pre_pandemic: z.union([z.string(), z.number()]).optional(),
	last_six_months_inquiries: z.union([z.string(), z.number()]).optional(),
	utilization_post_pandemic: z.union([z.string(), z.number()]).optional(),
	curp: z.string().optional()
});

const customersArraySchema = z.array(customerRecordSchema);

interface ValidationError {
	customerIndex: number;
	clientId?: string;
	errors: Array<{ field: string; message: string }>;
}

const SyncClients: React.FC = () => {
	const t = useTranslations('SyncClients');
	const navigate = useNavigate();
	const language = getLanguageFromPath(window.location.pathname) || DEFAULT_LANGUAGE;
	const supabase = useSupabaseStore((state) => state.supabase);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [customers, setCustomers] = useState<CustomerRecord[]>([]);
	const [fileName, setFileName] = useState<string>('');
	const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
	const [sendWelcomeTemplate, setSendWelcomeTemplate] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	const normalizeHeader = (header: string): string => {
		const headerMap: Record<string, string> = {
			'Started At': 'started_at',
			'Aprobado el': 'approved_at',
			'Approved At': 'approved_at',
			'Open Current Balance': 'open_current_balance',
			'Open Overdue Balance': 'open_overdue_balance',
			'Closed Max Balance': 'closed_maximum_balance',
			'Monthly Payment': 'monthly_payment',
			'Default Score': 'credit_bureau_default_score',
			'Score Default': 'credit_bureau_default_score',
			'Total Trials': 'total_trials',
			'Months Overdue': 'months_overdue',
			'Years in Bureau': 'years_in_credit_bureau',
			'Util. Pre-Pandemic': 'utilization_pre_pandemic',
			'Util. Pre-Pandemia': 'utilization_pre_pandemic',
			'6M Inquiries': 'last_six_months_inquiries',
			'Consultas 6M': 'last_six_months_inquiries',
			'Util. Post-Pandemic': 'utilization_post_pandemic',
			'Util. Post-Pandemia': 'utilization_post_pandemic'
		};

		return headerMap[header] || header.toLowerCase().replace(/\s+/g, '_').replace(/[.-]/g, '_');
	};

	const formatDateForBackend = (value: string, field: string): string => {
		if (!value || value.trim() === '') {
			return value;
		}

		const trimmedValue = value.trim();

		if (field === 'date_of_birth') {
			const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
			if (dateOnlyRegex.test(trimmedValue)) {
				return trimmedValue;
			}
			const date = new Date(trimmedValue);
			if (!isNaN(date.getTime())) {
				const year = date.getUTCFullYear();
				const month = String(date.getUTCMonth() + 1).padStart(2, '0');
				const day = String(date.getUTCDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			}
			return trimmedValue;
		}

		if (field === 'started_at' || field === 'approved_at') {
			const backendRegex = /^\d{4}-\d{2}-\d{2}(\s+\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2})?)?$/;
			if (backendRegex.test(trimmedValue)) {
				if (trimmedValue.endsWith('+00') || trimmedValue.endsWith('-00')) {
					return trimmedValue.replace(/[+-]00$/, '');
				}
				return trimmedValue;
			}

			const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
			if (dateOnlyRegex.test(trimmedValue)) {
				return trimmedValue;
			}

			const microsecondsMatch = trimmedValue.match(/\.(\d{1,6})/);
			const preservedMicroseconds = microsecondsMatch
				? microsecondsMatch[1].padEnd(6, '0').substring(0, 6)
				: '000000';

			const date = new Date(trimmedValue);
			if (!isNaN(date.getTime())) {
				const year = date.getUTCFullYear();
				const month = String(date.getUTCMonth() + 1).padStart(2, '0');
				const day = String(date.getUTCDate()).padStart(2, '0');
				const hours = String(date.getUTCHours()).padStart(2, '0');
				const minutes = String(date.getUTCMinutes()).padStart(2, '0');
				const seconds = String(date.getUTCSeconds()).padStart(2, '0');
				return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${preservedMicroseconds}`;
			}
			return trimmedValue;
		}

		return trimmedValue;
	};

	const parseValue = (value: string, header: string): string | number => {
		if (!value || value === '') return value;

		const dateFields = ['started_at', 'approved_at', 'date_of_birth'];

		if (dateFields.includes(header)) {
			return formatDateForBackend(value, header);
		}

		const numericFields = [
			'open_current_balance',
			'open_overdue_balance',
			'closed_maximum_balance',
			'monthly_payment',
			'credit_bureau_default_score',
			'total_trials',
			'months_overdue',
			'years_in_credit_bureau',
			'utilization_pre_pandemic',
			'last_six_months_inquiries',
			'utilization_post_pandemic'
		];

		if (numericFields.includes(header)) {
			const numValue = parseFloat(value);
			if (!isNaN(numValue) && isFinite(numValue)) {
				return numValue;
			}
		}

		return value;
	};

	const parseCsvToJson = (csvText: string): any[] => {
		const lines = csvText.trim().split('\n');
		if (lines.length < 2) return [];

		const rawHeaders = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
		const headers = rawHeaders.map(normalizeHeader);
		const customers: any[] = [];

		for (let i = 1; i < lines.length; i++) {
			const values: string[] = [];
			let currentValue = '';
			let insideQuotes = false;

			for (let j = 0; j < lines[i].length; j++) {
				const char = lines[i][j];

				if (char === '"') {
					insideQuotes = !insideQuotes;
				} else if (char === ',' && !insideQuotes) {
					values.push(currentValue.trim());
					currentValue = '';
				} else {
					currentValue += char;
				}
			}
			values.push(currentValue.trim());

			const customer: any = {};
			headers.forEach((header, index) => {
				const rawValue = values[index]?.replace(/^"|"$/g, '').trim();
				if (rawValue !== undefined && rawValue !== '') {
					customer[header] = parseValue(rawValue, header);
				}
			});

			if (Object.keys(customer).length > 0) {
				customers.push(customer);
			}
		}

		return customers;
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setFileName(file.name);
		setValidationErrors([]);
		setSyncResult(null);
		setErrorMessage('');

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const content = e.target?.result as string;
				let json: any;

				if (file.name.endsWith('.csv')) {
					json = parseCsvToJson(content);
				} else {
					json = JSON.parse(content);
				}

				const result = customersArraySchema.safeParse(json);

				if (!result.success) {
					const errors: ValidationError[] = [];
					result.error.issues.forEach((err: z.ZodIssue) => {
						const path = err.path;
						const customerIndex = typeof path[0] === 'number' ? path[0] : -1;
						const field = path[1] as string;

						const existingError = errors.find((e) => e.customerIndex === customerIndex);
						if (existingError) {
							existingError.errors.push({ field, message: err.message });
						} else {
							errors.push({
								customerIndex,
								clientId: customerIndex >= 0 && json[customerIndex]?.client_id,
								errors: [{ field, message: err.message }]
							});
						}
					});
					setValidationErrors(errors);
					setCustomers([]);
				} else {
					console.log('result.data', result.data);
					setCustomers(result.data);
					setValidationErrors([]);
				}
			} catch {
				setErrorMessage(file.name.endsWith('.csv') ? t('InvalidCsvFile') : t('InvalidJsonFile'));
				setCustomers([]);
			}
		};
		reader.readAsText(file);
	};

	const handleSyncClick = () => {
		setShowConfirmModal(true);
	};

	const handleConfirmSync = async () => {
		setShowConfirmModal(false);
		await handleSync(customers);
	};

	const handleSync = async (customersToSync: CustomerRecord[]) => {
		if (!supabase || customersToSync.length === 0) return;

		setIsLoading(true);
		setErrorMessage('');
		setSyncResult(null);
		setValidationErrors([]);

		try {
			const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

			const {
				data: { session }
			} = await supabase.auth.getSession();
			const token = session?.access_token;

			if (!token) {
				setErrorMessage(t('ErrorSyncingCustomers'));
				return;
			}

			const response = await fetch(`${supabaseUrl}/functions/v1/sync-finclu-customers`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify({
					customers: customersToSync,
					send_welcome_template: sendWelcomeTemplate
				} as SyncFincluCustomersRequest)
			});

			const data = await response.json();

			if (!response.ok || (data && !data.success)) {
				const validationError = data as ValidationErrorResponse;
				console.log('validationError', validationError);
				if (validationError.customer_errors && validationError.customer_errors.length > 0) {
					const errors: ValidationError[] = validationError.customer_errors.map((custError) => ({
						customerIndex: custError.customer_index,
						clientId: custError.client_id,
						errors: custError.errors
					}));
					setValidationErrors(errors);
					setErrorMessage(t('ServerValidationError'));
				} else if (validationError.general_errors && validationError.general_errors.length > 0) {
					setErrorMessage(validationError.general_errors.join(', '));
				} else {
					setErrorMessage(validationError.message || validationError.error);
				}
			} else {
				setSyncResult(data as SyncResult);
			}
		} catch (err) {
			console.error('Error syncing customers:', err);
			setErrorMessage(t('ErrorSyncingCustomers'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleRetryFailed = () => {
		if (!syncResult || syncResult.failed_records.length === 0) return;

		const failedClientIds = syncResult.failed_records.map((fr) => fr.client_id);
		const failedCustomers = customers.filter((c) => failedClientIds.includes(c.client_id));

		handleSync(failedCustomers);
	};

	const resetForm = () => {
		setCustomers([]);
		setFileName('');
		setValidationErrors([]);
		setSyncResult(null);
		setErrorMessage('');
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const renderCustomerName = (customer: CustomerRecord) => {
		const { names, first_last_name, second_last_name } = customer;
		return [names, first_last_name, second_last_name].filter(Boolean).join(' ').trim() || '-';
	};

	return (
		<div className="container mx-auto p-8">
			<div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
				<button
					onClick={() => navigate(`/${language}/center/support-clients`)}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						padding: '8px',
						border: 'none',
						background: 'none',
						cursor: 'pointer',
						color: '#3B82F6'
					}}
				>
					<ArrowLeft size={20} />
				</button>
			</div>
			<div className="mb-6 flex flex-col gap-6">
				<Typography type="display" size="md">
					{t('Title')}
				</Typography>
				<Typography size="lg" color="secondary" weight="regular">
					{t('Description')}
				</Typography>
			</div>

			{!syncResult && (
				<div className="bg-white rounded-lg shadow p-6 mb-6">
					<FileUploadSection
						fileName={fileName}
						onFileChange={handleFileChange}
						fileInputRef={fileInputRef}
						t={tForComponent(t)}
					/>

					<ValidationErrorsList errors={validationErrors} t={tForComponent(t)} />

					<ErrorMessageDisplay message={errorMessage} />

					{customers.length > 0 && (
						<>
							<CustomersPreviewTable
								customers={customers}
								renderCustomerName={renderCustomerName}
								t={tForComponent(t)}
							/>

							<div className="mb-4">
								<label className="flex items-center space-x-2">
									<input
										type="checkbox"
										checked={sendWelcomeTemplate}
										onChange={(e) => setSendWelcomeTemplate(e.target.checked)}
										className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
									/>
									<Typography size="sm">{t('SendWelcomeTemplate')}</Typography>
								</label>
							</div>

							<div className="flex gap-4 justify-end">
								<Button
									onClick={resetForm}
									disabled={isLoading}
									label={t('Reset')}
									size="auto"
									color="secondary"
									outlined
								/>
								<Button
									onClick={handleSyncClick}
									disabled={isLoading}
									loading={isLoading}
									label={t('SyncCustomers')}
									size="auto"
								/>
							</div>
						</>
					)}
				</div>
			)}

			<ConfirmModal
				isOpen={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onCancel={() => setShowConfirmModal(false)}
				onConfirm={handleConfirmSync}
				title={t('ConfirmSyncTitle')}
				subtitle={t('ConfirmSyncSubtitle', { count: customers.length })}
				confirmActionText={t('SyncCustomers')}
				type="confirm"
			>
				{sendWelcomeTemplate && (
					<div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
						<Typography size="sm" weight="medium">
							{t('WelcomeTemplateDisclaimer')}
						</Typography>
					</div>
				)}
			</ConfirmModal>

			{syncResult && (
				<SyncResultsDisplay
					syncResult={syncResult}
					isLoading={isLoading}
					onRetryFailed={handleRetryFailed}
					onReset={resetForm}
					t={tForComponent(t)}
				/>
			)}
		</div>
	);
};

export default SyncClients;
